import type { Session } from '@supabase/supabase-js';
import { supabase } from './authBootstrap';

type EmailIntent = 'invite' | 'recovery';
export interface EmailLinkSession { session: Session; intent: EmailIntent | null }

// Keep only the current landing in memory. React effect replay must not consume
// a one-use code twice. Never persist the link or fall back to an unrelated session.
let current: { href: string; promise: Promise<EmailLinkSession> } | undefined;

export function establishEmailLinkSession(href: string, allowExistingSession = false): Promise<EmailLinkSession> {
  // A bare password form must re-read the live session on each visit.
  const url = new URL(href);
  if (!url.search && !url.hash) return establish(href, allowExistingSession);
  if (current?.href === href) return current.promise;
  const promise = establish(href, allowExistingSession);
  current = { href, promise };
  return promise;
}

async function establish(href: string, allowExistingSession: boolean): Promise<EmailLinkSession> {
  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.slice(1));
  const param = (key: string) => url.searchParams.get(key) || hash.get(key);
  // Even empty credentials identify an attempted email verification. A malformed
  // reset link must not unlock the form for another account's stored session.
  const hasLinkParameters = ['code', 'token_hash', 'token', 'access_token', 'refresh_token',
    'type', 'error', 'error_code', 'error_description']
    .some(key => url.searchParams.has(key) || hash.has(key));
  // These old destinations were used exclusively by password recovery senders.
  const legacyRecovery = url.pathname === '/reset-password' || url.pathname === '/auth/verify';
  const rawType = param('type');
  if (rawType && rawType !== 'invite' && rawType !== 'recovery') {
    throw new Error('This email link has an unsupported verification type. Please request a new link.');
  }
  let intent: EmailIntent | null = rawType as EmailIntent | null;
  const code = param('code');
  const tokenHash = param('token_hash');
  const token = param('token');
  const accessToken = hash.get('access_token');
  const refreshToken = hash.get('refresh_token');
  if ([Boolean(code), Boolean(tokenHash || token), Boolean(accessToken || refreshToken)].filter(Boolean).length > 1 || (tokenHash && token)) {
    throw new Error('This email link contains conflicting verification details. Please request a new link.');
  }
  if (param('error') || param('error_code')) {
    throw new Error('This email link is invalid or expired. Please request a new link.');
  }
  if (!intent && legacyRecovery) intent = 'recovery';

  let session: Session | null = null;
  if (code) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw new Error('This link could not be verified. Open it in the browser where you requested it, or request a new link.');
    session = data.session;
    // Supabase returns the intent saved alongside the local PKCE verifier.
    const redirectType = (data as typeof data & { redirectType?: string | null }).redirectType;
    if (redirectType === 'recovery') intent = 'recovery';
  } else if (tokenHash || token) {
    if (!intent) throw new Error('This email link is missing its verification type. Please request a new link.');
    let email = url.searchParams.get('email');
    if (!email && token && !tokenHash) {
      try { email = localStorage.getItem('pendingResetEmail'); } catch { /* URL email still supported */ }
    }
    if (!tokenHash && !email) throw new Error('This link needs the email address used to request it. Please request a new link.');
    const { data, error } = tokenHash
      ? await supabase.auth.verifyOtp({ token_hash: tokenHash, type: intent })
      : await supabase.auth.verifyOtp({ token: token!, email: email!, type: intent });
    if (error) throw new Error('This email link is invalid or expired. Please request a new link.');
    session = data.session;
    try { localStorage.removeItem('pendingResetEmail'); } catch { /* optional legacy storage */ }
  } else if (accessToken || refreshToken) {
    if (!accessToken || !refreshToken) throw new Error('This email link is incomplete. Please request a new link.');
    const { data, error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
    if (error) throw new Error('This email link is invalid or expired. Please request a new link.');
    session = data.session;
  } else if (allowExistingSession && !hasLinkParameters) {
    const { data, error } = await supabase.auth.getSession();
    if (!error) session = data.session;
  }
  if (!session) throw new Error('Please request a new email link to continue.');
  return { session, intent };
}

export function cleanEmailLinkUrl(): void {
  const url = new URL(window.location.href);
  for (const key of ['code', 'token', 'token_hash', 'type', 'email', 'error', 'error_code', 'error_description']) url.searchParams.delete(key);
  url.hash = '';
  window.history.replaceState(window.history.state, '', url.toString());
}
